import React, { useMemo, useState } from "react";
import { Form, Select, Spin } from "antd";
import { debounce, uniqBy } from "lodash";
import { checkIsLoading, getSelectOptions, selectFilterOption } from "../../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../..";
import { fetchAccounts } from "../../../store/slices/agreementsSearch/agreementsSearch";
import { AnyObject } from "../../../lib/interfaces";
import { IClientType } from "../../../lib/types";
import "./ActiveSearchFormItem.less";
import { shortNames } from "../../../lib/const/shortsNames";

/*
  FormItem для поиска по Тех. ID, ИНН или БИН для форм расширенного поиска в массовых операциях
*/

interface ComponentProps {
  name: "organization.property.stringValue" | "subscriber.externalId" | "subscribers";
  onSelect: (props: AnyObject) => void;
  clientType: IClientType;
  minLengthSearch?: number;
}

export const ActiveSearchFormItem = (props: ComponentProps) => {
  const { name, onSelect, clientType, minLengthSearch } = props;

  const { tenant } = useAppSelector((state) => state.tenants);
  const { accounts, accountsLoading } = useAppSelector((state) => state.agreementsSearch);

  const [searchValue, setSearchValue] = useState<string>("");

  const propertyType: { alias: "BIN" | "INN"; rus: string } = useMemo(
    () => ({
      alias: tenant?.id === 1 ? "BIN" : "INN",
      rus: tenant?.id === 1 ? "БИН" : "ИНН",
    }),
    [tenant]
  );

  const dispatch = useAppDispatch();

  const label = useMemo(() => {
    switch (name) {
      case "organization.property.stringValue":
        return propertyType.rus;
      case "subscriber.externalId":
      case "subscribers":
        return shortNames.externalId;
    }
  }, [propertyType, name]);

  const placeholder = useMemo(() => {
    switch (name) {
      case "organization.property.stringValue":
        return `Введите ${propertyType.rus}`;
      case "subscriber.externalId":
      case "subscribers":
        return `Введите Тех. ID`;
    }
  }, [propertyType, name]);

  const debouncedSearch = debounce((value: string) => {
    if (value) {
      if (minLengthSearch && value.length < minLengthSearch) return;
      switch (name) {
        case "subscriber.externalId":
        case "subscribers":
          return dispatch(
            fetchAccounts({
              externalId: value,
              clientType,
            })
          );
        case "organization.property.stringValue":
          return dispatch(
            fetchAccounts({
              property: {
                stringValue: value,
                typeAlias: propertyType.alias,
              },
            })
          );
      }
    }
  }, 800);

  // Для ИНН/БИН ограничение по длине ввода
  const searchLengthLimit = useMemo(() => {
    if (name === "organization.property.stringValue") {
      switch (propertyType.alias) {
        case "INN":
          return 10;
        case "BIN":
          return 12;
      }
    }
  }, [name, propertyType]);

  const onSearch = (value: string) => {
    if (searchLengthLimit) {
      setSearchValue((prev) => {
        if (value.length > searchLengthLimit) {
          return prev;
        }
        return value;
      });
    } else {
      setSearchValue(value);
    }
    debouncedSearch(value);
  };

  const selectOptions = useMemo(() => {
    switch (name) {
      case "subscribers":
      case "subscriber.externalId":
        const externalIds: { externalId: string; accountId: number }[] = [];
        accounts.forEach(({ subscribers, id }) => {
          // Из массива externalId, нужно взять только те, которые соответствуют вводу
          subscribers
            .filter(({ externalId }) => {
              if (searchValue) {
                return externalId.startsWith(searchValue);
              }
              return false;
            })
            .forEach(({ externalId }) => {
              externalIds.push({ externalId, accountId: id });
            });
        });
        return getSelectOptions({
          list: uniqBy(externalIds, "externalId"),
          valueName: "externalId",
          labelName: "externalId",
        });

      case "organization.property.stringValue":
        const propertyList: { stringValue?: string; accountId: number }[] = [];
        if (clientType === "ORGANIZATION") {
          accounts.forEach(({ id, organization }) => {
            const innProperty = organization.properties.find(
              ({ propertyTypeAlias, propertyType: propertyTypeOrg }) =>
                propertyTypeAlias === propertyType.alias ||
                propertyTypeOrg?.alias === propertyType.alias
            );
            if (searchValue && innProperty) {
              propertyList.push({ accountId: id, stringValue: innProperty.stringValue });
            }
          });
        }
        return getSelectOptions({
          list: propertyList,
          valueName: "stringValue",
          labelName: "stringValue",
        });
    }
  }, [accounts, searchValue, propertyType]);

  return (
    <div className="active-search-formItem">
      <Form.Item style={{ margin: 0 }} shouldUpdate label={label}>
        {({ setFieldsValue, getFieldValue }) => {
          const onChange = (value, option) => {
            if (value) {
              onSelect(option.option);
              setFieldsValue({ [name]: value });
            }
          };
          const value = getFieldValue(name) || undefined;

          return (
            <div>
              <Form.Item
                name={name}
                getValueFromEvent={
                  () =>
                    undefined /* некорректно отображение при мультиселекте, поэтому нужно обнулять */
                }
              >
                <Select
                  showSearch
                  showArrow={false}
                  allowClear
                  style={{ width: "100%" }}
                  notFoundContent={checkIsLoading(accountsLoading) && <Spin size="small" />}
                  onSearch={onSearch}
                  placeholder={placeholder}
                  onChange={onChange}
                  value={value}
                  options={selectOptions}
                  filterOption={selectFilterOption}
                  searchValue={searchValue}
                  disabled={
                    name === "organization.property.stringValue" && clientType === "INDIVIDUAL"
                  }
                />
              </Form.Item>
            </div>
          );
        }}
      </Form.Item>
    </div>
  );
};
